/**
 * Registers all of the content components representing pages and blocks. They
 * need to be globally registered because they are dynamically rendered by the
 * component selectors (`PageComponentSelector` and `BlockComponentSelector`).
 */
import Vue from 'vue';

import router from '@/Scripts/router';
import store from '@/Scripts/store';
import { sync } from 'vuex-router-sync';
sync(store, router);

// `epiMessages` does not export anything but registers the `beta/contentSaved`
// and `beta/epiReady` message handlers that updates the vuex store.
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/import#Import_a_module_for_its_side_effects_only
import '@/Scripts/epiMessages';

// generate svg sprite from all files in /Assets/Images/SVG
//const files = require.context('@/Images/svg-sprites', true, /.*\.svg$/);
//files.keys().forEach(files);

// Episerver helpers
import EpiEdit from '@/Scripts/directives/epiEdit';
Vue.directive('epi-edit', EpiEdit);

// UUID
import UUID from 'vue-uuid';
Vue.use(UUID);

// Tailwind
// import 'tailwindcss/tailwind.css';
import '@/Styles/main.css';

// Page Templates
import DefaultTemplate from '@/Scripts/components/templates/DefaultTemplate.vue';

// Pages
import StartPage from '@/Scripts/components/pages/StartPage.vue';
import LoginPage from '@/Scripts/components/pages/LoginPage.vue';
import KategoriPage from '@/Scripts/components/pages/KategoriPage.vue';
import SearchPage from '@/Scripts/components/pages/SearchPage.vue';
import TiltakPage from '@/Scripts/components/pages/TiltakPage.vue';
import TiltakHovedPage from '@/Scripts/components/pages/TiltakHovedPage.vue';
import LatestPublicationsPage from '@/Scripts/components/pages/LatestPublicationsPage.vue';
import HandlingStandardPage from '@/Scripts/components/pages/handling/HandlingStandardPage.vue';
import SkoleStandardPage from '@/Scripts/components/pages/skole/SkoleStandardPage.vue';
import InnsatsStandardPage from '@/Scripts/components/pages/innsats/InnsatsStandardPage.vue';
import ForebyggingStandardPage from '@/Scripts/components/pages/forebygging/ForebyggingStandardPage.vue';
import LinkPage from '@/Scripts/components/pages/forebygging/LinkPage.vue';
import DictionaryPage from '@/Scripts/components/pages/forebygging/DictionaryPage.vue';
import CourseCalendarPage from '@/Scripts/components/pages/course/CourseCalendarPage.vue';
import CourseCalendarEventPage from '@/Scripts/components/pages/course/CourseCalendarEventPage.vue';
import ListPage from '@/Scripts/components/pages/ListPage.vue';
import UniversalPage from '@/Scripts/components/pages/UniversalPage.vue';
import FaqItemPage from '@/Scripts/components/pages/forebygging/FaqItemPage.vue';
import EducationMainPage from '@/Scripts/components/pages/EducationMainPage.vue';
import EducationPage from '@/Scripts/components/pages/EducationPage.vue';
import InteractiveMatrixPage from '@/Scripts/components/pages/InteractiveMatrixPage.vue';

// Blocks
import GenericBlock from '@/Scripts/components/blocks/GenericBlock.vue';
import FormContainerBlock from '@/Scripts/components/blocks/FormContainerBlock/FormContainerBlock.vue';
import ArticleSliderBlock from '@/Scripts/components/blocks/ArticleSliderBlock/ArticleSliderBlock.vue';
import ImageBlock from '@/Scripts/components/blocks/ImageBlock/ImageBlock.vue';
import ImageOnlyBlock from '@/Scripts/components/blocks/ImageOnlyBlock/ImageOnlyBlock.vue';
import ImageSliderBlock from '@/Scripts/components/blocks/ImageSliderBlock/ImageSliderBlock.vue';
import LinkButtonBlock from '@/Scripts/components/blocks/LinkButtonBlock/LinkButtonBlock.vue';
import LatestPublicationsBlock from '@/Scripts/components/blocks/LatestPublicationsBlock/LatestPublicationsBlock.vue';
import ShortcutsBlock from '@/Scripts/components/blocks/ShortcutsBlock/ShortcutsBlock.vue';
import SubpageListBlock from '@/Scripts/components/blocks/SubpageListBlock/SubpageListBlock.vue';
import NavigationBlock from '@/Scripts/components/blocks/NavigationBlock/NavigationBlock.vue';
import ArticleDisplayBlock from '@/Scripts/components/blocks/ArticleDisplayBlock/ArticleDisplayBlock.vue';
import EducationTargetGroupBlock from '@/Scripts/components/blocks/EducationTargetGroupBlock/EducationTargetGroupBlock.vue';
import HtmlBlock from '@/Scripts/components/blocks/HtmlBlock/HtmlBlock.vue';
import SloganBlock from '@/Scripts/components/blocks/SloganBlock/SloganBlock.vue';
import ImageWithButtonsBlock from '@/Scripts/components/blocks/ImageWithButtonsBlock/ImageWithButtonsBlock.vue';

// Views
import Preview from '@/Scripts/components/Preview.vue';
import DefaultPage from '@/Scripts/components/DefaultPage.vue';

// Fontawesome
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
library.add(fas, fab);

// Tippy (tooltip)
import VueTippy, { TippyComponent } from 'vue-tippy';
Vue.use(VueTippy);
Vue.component('tippy', TippyComponent);

// Scroll to https://vue-scrollto.netlify.app/
// var VueScrollTo = require('vue-scrollto');
import VueScrollTo from 'vue-scrollto';
Vue.use(VueScrollTo);

// Templates
Vue.component('DefaultTemplate', DefaultTemplate);

// Pages
Vue.component('StartPage', StartPage);
Vue.component('LoginPage', LoginPage);
Vue.component('KategoriPage', KategoriPage);
Vue.component('SearchPage', SearchPage);
Vue.component('TiltakPage', TiltakPage);
Vue.component('TiltakHovedPage', TiltakHovedPage);
Vue.component('LatestPublicationsPage', LatestPublicationsPage);
Vue.component('HandlingStandardPage', HandlingStandardPage);
Vue.component('SkoleStandardPage', SkoleStandardPage);
Vue.component('InnsatsStandardPage', InnsatsStandardPage);
Vue.component('ForebyggingStandardPage', ForebyggingStandardPage);
Vue.component('LinkPage', LinkPage);
Vue.component('DictionaryPage', DictionaryPage);
Vue.component('CourseCalendarPage', CourseCalendarPage);
Vue.component('CourseCalendarEventPage', CourseCalendarEventPage);
Vue.component('ListPage', ListPage);
Vue.component('UniversalPage', UniversalPage);
Vue.component('FaqItemPage', FaqItemPage);
Vue.component('EducationMainPage', EducationMainPage);
Vue.component('EducationPage', EducationPage);
Vue.component('InteractiveMatrixPage', InteractiveMatrixPage);

// Blocks
Vue.component('GenericBlock', GenericBlock);
Vue.component('FormContainerBlock', FormContainerBlock);
Vue.component('ArticleSliderBlock', ArticleSliderBlock);
Vue.component('ImageBlock', ImageBlock);
Vue.component('ImageOnlyBlock', ImageOnlyBlock);
Vue.component('ImageSliderBlock', ImageSliderBlock);
Vue.component('LinkButtonBlock', LinkButtonBlock);
Vue.component('LatestPublicationsBlock', LatestPublicationsBlock);
Vue.component('ShortcutsBlock', ShortcutsBlock);
Vue.component('SubpageListBlock', SubpageListBlock);
Vue.component('NavigationBlock', NavigationBlock);
Vue.component('ArticleDisplayBlock', ArticleDisplayBlock);
Vue.component('EducationTargetGroupBlock', EducationTargetGroupBlock);
Vue.component('HtmlBlock', HtmlBlock);
Vue.component('SloganBlock', SloganBlock);
Vue.component('ImageWithButtonsBlock', ImageWithButtonsBlock);

// Views
Vue.component('Preview', Preview);
Vue.component('DefaultPage', DefaultPage);

// Other
Vue.component('font-awesome-icon', FontAwesomeIcon);

// Filters
import { truncate } from './vueFilters.js';
Vue.filter('truncate', truncate);

/* eslint-disable-next-line no-unused-vars */
let App = new Vue({
    el: '#App',
    store,
    router
});
